import { useEffect, useMemo, useState } from 'react';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Notify } from 'notiflix';
import { ENDPOINT } from 'constants';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';

const EMPTY_RULE = {
  label: '',
  triggerTemplateKey: null,
  notifyTemplateKey: null,
  isEnabled: true,
  emailsText: '',
};

const templateLabel = (tpl) => (tpl ? `${tpl.name} (${tpl.templateKey})` : '');

// The trigger/notify picker only needs templateKey + name/reminderType for its label,
// so a plain Autocomplete over the full template list is enough — no dedicated
// backend endpoint required beyond the one Email Templates already uses.
const TemplatePicker = ({ label, helperText, templates, value, onChange, error }) => (
  <Autocomplete
    options={templates}
    getOptionLabel={templateLabel}
    isOptionEqualToValue={(opt, val) => opt.templateKey === val?.templateKey}
    value={templates.find((t) => t.templateKey === value) || null}
    onChange={(_, newValue) => onChange(newValue?.templateKey || null)}
    groupBy={(opt) => opt.reminderType}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        helperText={error || helperText}
        error={!!error}
        fullWidth
      />
    )}
  />
);

const RuleFormDialog = ({ open, onClose, onSubmit, templates, initial, saving }) => {
  const [form, setForm] = useState(EMPTY_RULE);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              label: initial.label || '',
              triggerTemplateKey: initial.triggerTemplateKey,
              notifyTemplateKey: initial.notifyTemplateKey,
              isEnabled: initial.isEnabled,
              emailsText: (initial.recipientEmails || []).join(', '),
            }
          : EMPTY_RULE,
      );
      setErrors({});
    }
  }, [open, initial]);

  const parsedEmails = form.emailsText
    .split(',')
    .map((e) => e.trim())
    .filter(Boolean);

  const handleSave = () => {
    const nextErrors = {};
    if (!form.triggerTemplateKey) nextErrors.triggerTemplateKey = 'Pick the template that triggers this rule';
    if (!form.notifyTemplateKey) nextErrors.notifyTemplateKey = 'Pick the template admins actually receive';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSubmit({
      label: form.label.trim() || null,
      triggerTemplateKey: form.triggerTemplateKey,
      notifyTemplateKey: form.notifyTemplateKey,
      isEnabled: form.isEnabled,
      recipientEmails: parsedEmails,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Notification Rule' : 'New Notification Rule'}</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2.5} pt={1}>
          <TextField
            label="Label (optional)"
            placeholder="e.g. Notify ops on new signup"
            fullWidth
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
          />

          <TemplatePicker
            label="When this customer email is sent…"
            helperText="Any of the customer-facing templates — this is the trigger"
            templates={templates}
            value={form.triggerTemplateKey}
            onChange={(key) => setForm((f) => ({ ...f, triggerTemplateKey: key }))}
            error={errors.triggerTemplateKey}
          />

          <TemplatePicker
            label="…notify admins using this template"
            helperText="A separate template — its own subject/body is what admins receive"
            templates={templates}
            value={form.notifyTemplateKey}
            onChange={(key) => setForm((f) => ({ ...f, notifyTemplateKey: key }))}
            error={errors.notifyTemplateKey}
          />

          <MDBox>
            <TextField
              fullWidth
              label="Recipient emails"
              placeholder="admin@tecnogex.com, ops@tecnogex.com"
              value={form.emailsText}
              onChange={(e) => setForm((f) => ({ ...f, emailsText: e.target.value }))}
              helperText="Comma-separated — every address here gets the notify template above"
            />
            {parsedEmails.length > 0 && (
              <MDBox display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                {parsedEmails.map((email) => (
                  <Chip key={email} label={email} size="small" variant="outlined" />
                ))}
              </MDBox>
            )}
          </MDBox>

          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="button" fontWeight="medium">Enabled</MDTypography>
            <Switch
              checked={form.isEnabled}
              onChange={(e) => setForm((f) => ({ ...f, isEnabled: e.target.checked }))}
            />
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>Cancel</MDButton>
        <MDButton variant="gradient" color="info" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Rule'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

const RuleCard = ({ rule, templatesByKey, onToggle, onEdit, onDelete, onSendTest }) => {
  const [testing, setTesting] = useState(false);
  const trigger = templatesByKey[rule.triggerTemplateKey];
  const notify = templatesByKey[rule.notifyTemplateKey];

  const handleTest = async () => {
    setTesting(true);
    await onSendTest(rule);
    setTesting(false);
  };

  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <MDTypography variant="button" fontWeight="bold" display="block">
          {rule.label || 'Untitled rule'}
        </MDTypography>
        <Switch
          checked={rule.isEnabled}
          onChange={(e) => onToggle(rule, e.target.checked)}
        />
      </MDBox>

      <MDBox mt={1.5} display="flex" flexDirection="column" gap={1}>
        <MDBox display="flex" alignItems="center" gap={1}>
          <Icon fontSize="small" color="secondary">bolt</Icon>
          <MDTypography variant="caption" color="secondary">Trigger</MDTypography>
        </MDBox>
        <MDTypography variant="caption" fontWeight="medium" sx={{ pl: 3.5, wordBreak: 'break-word' }}>
          {trigger ? templateLabel(trigger) : rule.triggerTemplateKey}
        </MDTypography>

        <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
          <Icon fontSize="small" color="secondary">mark_email_unread</Icon>
          <MDTypography variant="caption" color="secondary">Notifies admins with</MDTypography>
        </MDBox>
        <MDTypography variant="caption" fontWeight="medium" sx={{ pl: 3.5, wordBreak: 'break-word' }}>
          {notify ? templateLabel(notify) : rule.notifyTemplateKey}
        </MDTypography>
      </MDBox>

      <Divider sx={{ my: 2 }} />

      <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
        Recipients
      </MDTypography>
      {rule.recipientEmails?.length > 0 ? (
        <MDBox display="flex" flexWrap="wrap" gap={0.5}>
          {rule.recipientEmails.map((email) => (
            <Chip key={email} label={email} size="small" variant="outlined" />
          ))}
        </MDBox>
      ) : (
        <MDTypography variant="caption" color="warning">No recipients — this rule won&apos;t notify anyone</MDTypography>
      )}

      <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3}>
        <MDButton
          size="small"
          variant="outlined"
          color="info"
          disabled={testing || !rule.recipientEmails?.length || !rule.isEnabled}
          onClick={handleTest}
        >
          {testing ? 'Sending...' : 'Send test'}
        </MDButton>
        <MDBox display="flex" gap={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit(rule)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => onDelete(rule)}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      </MDBox>
    </Card>
  );
};

const AdminNotifications = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${user?.accessToken}` } };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rulesRes, templatesRes] = await Promise.all([
        axios.get(`${ENDPOINT}api/v1/admin/notification-rules`, authHeaders),
        axios.get(`${ENDPOINT}api/v1/admin/reminder-templates`, authHeaders),
      ]);
      setRules(rulesRes.data.data || []);
      setTemplates(templatesRes.data.data || []);
    } catch (err) {
      console.error('Failed to fetch admin notification rules:', err);
      Notify.failure('Failed to load admin notification rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const templatesByKey = useMemo(
    () => Object.fromEntries(templates.map((t) => [t.templateKey, t])),
    [templates],
  );

  const openCreate = () => { setEditingRule(null); setDialogOpen(true); };
  const openEdit = (rule) => { setEditingRule(rule); setDialogOpen(true); };

  const handleSubmit = async (payload) => {
    try {
      setSaving(true);
      if (editingRule) {
        const { data } = await axios.put(
          `${ENDPOINT}api/v1/admin/notification-rules/${editingRule.id}`,
          payload,
          authHeaders,
        );
        setRules((prev) => prev.map((r) => (r.id === editingRule.id ? data.data : r)));
        Notify.success('Rule updated');
      } else {
        const { data } = await axios.post(
          `${ENDPOINT}api/v1/admin/notification-rules`,
          payload,
          authHeaders,
        );
        setRules((prev) => [data.data, ...prev]);
        Notify.success('Rule created');
      }
      setDialogOpen(false);
    } catch (err) {
      console.error(err);
      Notify.failure(err.response?.data?.message || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (rule, isEnabled) => {
    // Optimistic — flip immediately, roll back if the request fails.
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, isEnabled } : r)));
    try {
      await axios.put(`${ENDPOINT}api/v1/admin/notification-rules/${rule.id}`, { isEnabled }, authHeaders);
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to update rule');
      setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, isEnabled: !isEnabled } : r)));
    }
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Delete the rule "${rule.label || rule.triggerTemplateKey}"?`)) return;
    try {
      await axios.delete(
        `${ENDPOINT}api/v1/admin/notification-rules/${rule.id}?label=${encodeURIComponent(rule.label || rule.triggerTemplateKey)}`,
        authHeaders,
      );
      setRules((prev) => prev.filter((r) => r.id !== rule.id));
      Notify.success('Rule deleted');
    } catch (err) {
      console.error(err);
      Notify.failure('Failed to delete rule');
    }
  };

  const handleSendTest = async (rule) => {
    try {
      const { data } = await axios.post(
        `${ENDPOINT}api/v1/admin/notification-rules/${rule.id}/test`,
        {},
        authHeaders,
      );
      Notify.success(data.message || 'Test notification sent');
    } catch (err) {
      console.error(err);
      Notify.failure(err.response?.data?.message || 'Failed to send test');
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
          >
            <MDBox>
              <MDTypography variant="h6" color="white">Admin Notifications</MDTypography>
              <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                Pick any customer email as a trigger, then choose what admins get told about it
              </MDTypography>
            </MDBox>
            <MDBox display="flex" gap={1}>
              <MDButton variant="outlined" color="white" onClick={() => navigate('/email-templates')}>
                <Icon sx={{ mr: 0.5 }} fontSize="small">description</Icon>
                Edit templates
              </MDButton>
              <MDButton variant="gradient" color="white" onClick={openCreate}>
                <Icon sx={{ mr: 0.5 }} fontSize="small">add</Icon>
                Add Rule
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox p={3}>
            <MDTypography variant="caption" color="secondary" display="block" mb={2}>
              Each rule fires automatically the moment its trigger email is sent to a customer — no
              other module needs to change. The notify template is separate content, written for admins,
              not a copy of what the customer received.
            </MDTypography>

            {loading ? (
              <MDBox display="flex" justifyContent="center" py={5}>
                <CircularProgress />
              </MDBox>
            ) : rules.length === 0 ? (
              <MDBox textAlign="center" py={5}>
                <Icon fontSize="large" color="secondary">notifications_off</Icon>
                <MDTypography variant="body2" color="secondary" mt={1}>
                  No rules yet — add one to start notifying admins about a customer event.
                </MDTypography>
              </MDBox>
            ) : (
              <Grid container spacing={3}>
                {rules.map((rule) => (
                  <Grid item xs={12} md={6} lg={4} key={rule.id}>
                    <RuleCard
                      rule={rule}
                      templatesByKey={templatesByKey}
                      onToggle={handleToggle}
                      onEdit={openEdit}
                      onDelete={handleDelete}
                      onSendTest={handleSendTest}
                    />
                  </Grid>
                ))}
              </Grid>
            )}
          </MDBox>
        </Card>
      </MDBox>

      <RuleFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        templates={templates}
        initial={editingRule}
        saving={saving}
      />
    </DashboardLayout>
  );
};

export default AdminNotifications;
